/**
 * Сервис для запроса разрешений от пользователя
 * Запрашивает все доступные разрешения при старте приложения
 */

export interface PermissionResults {
  camera?: PermissionState
  microphone?: PermissionState
  geolocation?: PermissionState
  notifications?: NotificationPermission
  accelerometer?: PermissionState | 'not-supported'
  gyroscope?: PermissionState | 'not-supported'
}

type PermissionState = 'granted' | 'denied' | 'prompt'

/**
 * Сервис для управления разрешениями
 */
export class PermissionsService {
  /**
   * Запрашивает все доступные разрешения
   * @returns Результаты запроса разрешений
   * 
   * ВАЖНО: НЕ запрашиваем камеру здесь, т.к. это может конфликтовать со Scanner
   * Камера запрашивается непосредственно в Scanner компоненте
   */
  static async requestAllPermissions(): Promise<PermissionResults> {
    const results: PermissionResults = {}
    
    // КАМЕРА И МИКРОФОН ПРОПУЩЕНЫ - они запрашиваются компонентами при необходимости
    // Это предотвращает конфликты и блокировки
    console.log('[PermissionsService] 📹 Skipping camera/microphone - will be requested by Scanner')
    
    // 1. Геолокация (для будущих фич - привязка к локации)
    try {
      await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 5000,
          enableHighAccuracy: false
        })
      })
      results.geolocation = 'granted'
      console.log('[PermissionsService] ✅ Geolocation permission granted')
    } catch (err) {
      results.geolocation = 'denied'
      console.warn('[PermissionsService] ⚠️ Geolocation permission denied')
    }
    
    // 2. Уведомления (для пуш-уведомлений)
    if ('Notification' in window) {
      try {
        const permission = await Notification.requestPermission()
        results.notifications = permission
        console.log(`[PermissionsService] ${permission === 'granted' ? '✅' : '⚠️'} Notifications permission: ${permission}`)
      } catch (err) {
        results.notifications = 'denied'
        console.warn('[PermissionsService] ⚠️ Notifications permission error')
      }
    }
    
    // 3. Акселерометр (Generic Sensor API)
    if ('Accelerometer' in window) {
      try {
        // @ts-ignore - Accelerometer API может быть не в типах
        const accel = new Accelerometer({ frequency: 60 })
        accel.start()
        results.accelerometer = 'granted'
        accel.stop()
        console.log('[PermissionsService] ✅ Accelerometer permission granted')
      } catch (err) {
        const error = err as Error
        if (error.name === 'NotAllowedError') {
          results.accelerometer = 'denied'
        } else {
          results.accelerometer = 'not-supported'
        }
        console.warn('[PermissionsService] ⚠️ Accelerometer not available')
      }
    } else {
      results.accelerometer = 'not-supported'
    }
    
    // 4. Гироскоп (Generic Sensor API)
    if ('Gyroscope' in window) {
      try {
        // @ts-ignore - Gyroscope API может быть не в типах
        const gyro = new Gyroscope({ frequency: 60 })
        gyro.start()
        results.gyroscope = 'granted'
        gyro.stop()
        console.log('[PermissionsService] ✅ Gyroscope permission granted')
      } catch (err) {
        const error = err as Error
        if (error.name === 'NotAllowedError') {
          results.gyroscope = 'denied'
        } else {
          results.gyroscope = 'not-supported'
        }
        console.warn('[PermissionsService] ⚠️ Gyroscope not available')
      }
    } else {
      results.gyroscope = 'not-supported'
    }
    
    return results
  }
  
  /**
   * Проверяет статус разрешений без запроса
   */
  static async checkPermissions(): Promise<Partial<PermissionResults>> {
    const results: Partial<PermissionResults> = {}
    
    // Проверка через Permissions API (если поддерживается)
    if ('permissions' in navigator) {
      try {
        const cameraPermission = await navigator.permissions.query({ name: 'camera' as PermissionName })
        results.camera = cameraPermission.state
      } catch {}
      
      try {
        const notificationPermission = await navigator.permissions.query({ name: 'notifications' as PermissionName })
        results.notifications = notificationPermission.state as NotificationPermission
      } catch {}
      
      try {
        const geolocationPermission = await navigator.permissions.query({ name: 'geolocation' as PermissionName })
        results.geolocation = geolocationPermission.state
      } catch {}
    }
    
    // Уведомления всегда доступны через Notification API
    if ('Notification' in window) {
      results.notifications = Notification.permission
    }
    
    return results
  }
}
